import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import { useTheme } from '../../../context/ThemeContext';
import {
  EMPTY_STATES,
  METRIC_LABELS,
  creatorDetailRoute,
} from '../constants';
import { DISPLAY_DATE_FORMAT } from '../utils/dateRange';
import { formatCount, formatCurrency, formatPercentage, percentOf } from '../utils/formatters';
import {
  FilterGroup,
  FilterLabel,
  FilterRow,
  WindowedCaption,
} from '../Styles';
import CreatorProgramTable from '../components/CreatorProgramTable';
import FilterSelect from '../components/FilterSelect';
import ListPagination from '../components/ListPagination';
import QueryErrorState from '../components/QueryErrorState';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { usePaginationState } from '../hooks/usePaginationState';
import { useStoreOptions } from '../hooks/useSelectOptions';
import { useLeaderboard } from '../hooks/useCreatorQueries';
import SummaryDetailPanel from '../CreatorDetail/SummaryDetailPanel';
import type { LeaderboardEntry } from '../types';
import type { DateRange } from '../../../commonComponents/DateRangePicker/types';

const { Text } = Typography;

/**
 * §1.5 — performance leaderboard.
 *
 * The API sorts by `summary.netCreatorEarnings` DESC before paginating, so rows are
 * rendered exactly in the order received. No `sorter` is attached to any column and
 * nothing is re-sorted client-side — doing so would reorder one page in isolation
 * and quietly misrepresent the ranking, since the sort happens across the whole set
 * before pagination.
 *
 * The rank column is therefore derived from the page offset, not from the data.
 */
const LeaderboardTab: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const { dateRange, setDateRange, apiRange } = useDateRangeFilter();
  const { page, pageSize, onPaginationChange, withPageReset } = usePaginationState();
  const [storeId, setStoreId] = React.useState<string | undefined>();

  const { options: storeOptions } = useStoreOptions();

  const params = useMemo(
    () => ({ ...apiRange, store: storeId, page, limit: pageSize }),
    [apiRange, storeId, page, pageSize]
  );

  const { data, isLoading, isFetching, isError, refetch } = useLeaderboard(params);

  const rows = data?.creators ?? [];
  const total = data?.total ?? 0;

  // Filters change what the server returns, so reset to page 1 alongside them.
  const handleRangeChange = useMemo(
    () => withPageReset<DateRange>(setDateRange),
    [withPageReset, setDateRange]
  );
  const handleStoreChange = useMemo(
    () => withPageReset<string | undefined>(setStoreId),
    [withPageReset]
  );

  const goToCreator = useCallback(
    (creatorId: string) => navigate(creatorDetailRoute(creatorId)),
    [navigate]
  );

  const columns: ColumnsType<LeaderboardEntry> = useMemo(
    () => [
      {
        title: '#',
        key: 'rank',
        fixed: 'left',
        width: 60,
        // Rank comes from the server's ordering plus the page offset.
        render: (_, __, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: 'CREATOR',
        key: 'creator',
        fixed: 'left',
        width: 200,
        render: (_, record) => (
          <div>
            <div style={{ fontWeight: 600 }}>{record.creator?.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.creator?.handle}
            </Text>
          </div>
        ),
      },
      {
        title: METRIC_LABELS.ORDERS.toUpperCase(),
        key: 'orders',
        width: 90,
        render: (_, record) => formatCount(record.summary?.orders),
      },
      {
        title: 'CREATOR EARNINGS',
        key: 'netCreatorEarnings',
        width: 160,
        render: (_, record) => (
          <strong>{formatCurrency(record.summary?.netCreatorEarnings)}</strong>
        ),
      },
      {
        title: 'CUSTOMER DISCOUNT',
        key: 'netCustomerDiscount',
        width: 165,
        render: (_, record) => formatCurrency(record.summary?.netCustomerDiscount),
      },
      {
        title: 'TOTAL COST',
        key: 'totalCost',
        width: 125,
        render: (_, record) => formatCurrency(record.summary?.totalCost),
      },
      {
        title: 'COST % OF BASE',
        key: 'costPercentOfBase',
        width: 140,
        render: (_, record) => formatPercentage(record.summary?.costPercentOfBase),
      },
      {
        title: 'AOV',
        key: 'aov',
        width: 110,
        render: (_, record) => formatCurrency(record.summary?.aov),
      },
      {
        // The headline metric, compacted to one column here; the detail screen and the
        // expanded row both give it the full treatment.
        title: 'NEW VS RETURNING',
        key: 'customerMix',
        width: 180,
        render: (_, record) => {
          const customers = record.summary?.customers;
          const newCount = customers?.new ?? 0;
          const returningCount = customers?.returning ?? 0;
          const unknownCount = customers?.unknown ?? 0;
          const totalCustomers = newCount + returningCount + unknownCount;

          if (totalCustomers === 0) return <Text type="secondary">—</Text>;

          const newShare = percentOf(newCount, totalCustomers);

          return (
            <Tooltip
              title={`${formatCount(newCount)} new · ${formatCount(
                returningCount
              )} returning · ${formatCount(unknownCount)} unknown`}
            >
              <div>
                <Progress
                  percent={newShare}
                  showInfo={false}
                  size="small"
                  strokeColor="#2D6CCA"
                  railColor={isDarkMode ? '#30363d' : '#e8e8e8'}
                />
                <Text style={{ fontSize: 12 }}>
                  {formatPercentage(newShare)} new ({formatCount(newCount)}/
                  {formatCount(totalCustomers)})
                </Text>
              </div>
            </Tooltip>
          );
        },
      },
    ],
    [page, pageSize, isDarkMode]
  );

  if (isError && rows.length === 0) {
    return (
      <QueryErrorState
        title="Could not load the leaderboard"
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <FilterRow style={{ marginBottom: 16 }}>
        <FilterGroup>
          <FilterLabel>Date range</FilterLabel>
          <DateRangePicker
            selectedDateRange={dateRange}
            onDateRangeChange={handleRangeChange}
          />
        </FilterGroup>

        <FilterSelect
          label="Store"
          placeholder="All stores"
          options={storeOptions}
          value={storeId}
          onChange={handleStoreChange}
        />
      </FilterRow>

      <div style={{ marginBottom: 12 }}>
        <Tag color="blue">Ranked by net creator earnings</Tag>
        {data?.range && (
          <WindowedCaption>
            {moment(data.range.startDate).format(DISPLAY_DATE_FORMAT)} →{' '}
            {moment(data.range.endDate).format(DISPLAY_DATE_FORMAT)}
          </WindowedCaption>
        )}
      </div>

      <CreatorProgramTable<LeaderboardEntry>
        columns={columns}
        dataSource={rows}
        rowKey={(record) => record.creator?.id}
        loading={isLoading || isFetching}
        emptyText={EMPTY_STATES.LEADERBOARD}
        expandable={{
          expandedRowRender: (record) =>
            record.summary ? <SummaryDetailPanel summary={record.summary} /> : null,
        }}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: () => goToCreator(record.creator.id),
        })}
      />

      <ListPagination
        visible={rows.length > 0}
        totalCount={total}
        currentPage={page}
        pageSize={pageSize}
        onChange={onPaginationChange}
        totalLabel="Total creators"
      />
    </>
  );
};

export default LeaderboardTab;
